import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { MemoryAppsIcons } from "../assets/Icons/MemoryAppsIcons";
import { CustomItem } from "../common/CustomItem/CustomItem";
import { MENU } from "../../constants/menu";
import { checkAccessFunctionality } from "../common/Privilege/FunctionPrivilegeCheck";
import { useDispatch } from "react-redux";
import { setApprovedDelivery } from "../../redux/stock/approvedDeliverySlice";
import { useAuth } from "../../modules/user/services/AuthProvider";

export const GlobalMenu: React.FC = () => {
  const dispatch = useDispatch();
  const { functionalities, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [clickedMenu, setClickedMenu] = useState("");

  const userRole = user?.role?.toUpperCase();
  const userDepartment = user?.department;

  // Filtrer les menus et sous-menus selon les fonctionnalités
  const filteredMenu = useMemo(() => {
    const storedFunctionalities =
      functionalities.length > 0
        ? functionalities
        : JSON.parse(localStorage.getItem("functionalities") || "[]");

    return MENU.map(menuItem => ({
      ...menuItem,
      subMenu: menuItem.subMenu.filter(subItem =>
        subItem.user_functionalities.length === 0 ||
        checkAccessFunctionality(
          storedFunctionalities,
          subItem.user_functionalities,
          userRole,
          userDepartment
        )
      ),
    })).filter(menuItem =>
      checkAccessFunctionality(
        storedFunctionalities,
        menuItem.user_functionalities,
        userRole,
        userDepartment
      ) && menuItem.subMenu.length > 0
    );
  }, [functionalities, userRole, userDepartment]);

  const { firstHalf, secondHalf } = useMemo(() => {
    const n = filteredMenu.length;
    if (n <= 1) return { firstHalf: filteredMenu, secondHalf: [] };

    const lines = (text: string) => Math.max(1, Math.ceil(text.trim().length / 28));
    const weights = filteredMenu.map(
      (m) =>
        lines(m.label) +
        m.subMenu.reduce((sum, s) => sum + lines(s.label), 0) +
        1 // espacement entre blocs
    );
    const total = weights.reduce((a, b) => a + b, 0);

    let bestSplit = 1;
    let bestMax = Infinity;
    let left = 0;
    for (let k = 1; k < n; k++) {
      left += weights[k - 1];
      const max = Math.max(left, total - left);
      if (max < bestMax) {
        bestMax = max;
        bestSplit = k;
      }
    }

    return {
      firstHalf: filteredMenu.slice(0, bestSplit),
      secondHalf: filteredMenu.slice(bestSplit),
    };
  }, [filteredMenu]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickMenu = useCallback(
    (label: string) => {
      setOpen(false);
      setClickedMenu(label);
      dispatch(setApprovedDelivery([]));
    },
    [dispatch]
  );

  return (
    <React.Fragment>
      <IconButton onClick={handleClickOpen}>
        <MemoryAppsIcons />
      </IconButton>
      <Dialog
        open={open}
        keepMounted
        onClose={handleClose}
        PaperProps={{
          sx: {
            position: "absolute",
            top: 50,
            right: 0,
            margin: 2,
            width: { sm: secondHalf.length > 0 ? "550px": "350px", xs: "92%" },
            height: { sm: "auto", xs: "92%" },
          },
        }}
      >
        <DialogTitle>{"Quick Access"}</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="row"
            gap={2}
            alignItems="stretch"
          >
            <Box flex={1}>
              {firstHalf.map((item, index) => (
                <Box key={`first-${index}`}>
                  <CustomItem
                    label={item.label}
                    onClick={handleClickMenu}
                    isActive={clickedMenu === item.label}
                    subMenu={item.subMenu}
                    suffix={item.label === "Warehouse"}
                  />
                </Box>
              ))}
            </Box>

            {secondHalf.length > 0 && (
              <Divider 
                orientation="vertical" 
                flexItem
                sx={{ borderRightWidth: 2 }} 
              />
            )}

            {secondHalf.length > 0 && (
              <Box flex={1}>
                {secondHalf.map((item, index) => (
                  <Box key={`second-${index}`}>
                    <CustomItem
                      label={item.label}
                      onClick={handleClickMenu}
                      isActive={clickedMenu === item.label}
                      subMenu={item.subMenu}
                      suffix={item.label === "Warehouse"}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};